import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QuoteEditData, QuoteExpenses } from "../../../../types/Quote";
import SelectFromServerData from "../../../../components/Form/El/SelectFromServerData";
import Calendar from "../../../../components/Form/El/Calendar";
import CheckboxNew from "../../../../components/Form/El/CheckboxNew";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  QUOTE_MISC_QUERY,
  QUOTE_MISC_QUERY_SUPPLIER,
} from "../../../Services/Queries/Common/CommonQueries";
import { catchNotification, setNotification } from "../../../../helpers/cache";
import ActionButton from "../../../../components/Buttons/ActionButton";
import { Params } from "../../../../types/Main";
import { onChangeParamsCallback } from "../../../../helpers/Main";
import Input from "../../../../components/Form/El/Input";
import { regExpNumber } from "../../../../helpers/RegExp";
import {
  CREATE_QUOTE_COMMENT,
  CREATE_QUOTE_EXPENSE,
  CREATE_QUOTE_MESSAGE,
  DELETE_QUOTE_EXPENSE,
  DELETE_QUOTE_FILES,
  UPDATE_QUOTE_EXPENSE,
  UPDATE_CASHBACK,
  UPDATE_QUOTE_FILES,
  CREATE_QUOTE_FILE_DIRECTORY,
  UPDATE_QUOTE_FILE_DIRECTORY,
  DELETE_QUOTE_FILE_DIRECTORY,
  SET_QUOTE_CUSTOMER_UPLOAD_BLOCK,
} from "../../Services/Mutations/Mutations";
import {
  QUOTE_FILES_REFRESH_QUERY,
  QUOTE_FILES_ZIP_LINK,
} from "../../Services/Queries/Queries";
import { LoadingDesign } from "../../../../helpers";
import QuoteDeleteModal from "./QuoteDeleteModal1";
import { capitalize, cloneDeep } from "lodash";
import { CreditOrder } from "../../../../types/CreditOrder";
import { Comments } from "../../../../types/Comments";
import { convertDateToUSFormat } from "../../../../helpers/utils";
import { FileQuote, QuoteFileDirectory } from "../../../../types/File";
import EditQuoteInputFiles from "./EditFilesInputFiles";
import QuoteFilesTree, {
  folderUid,
  formatSizeTotal,
  getFolderUids,
  sizeTotalTitle,
  sumFileSizes,
} from "./QuoteFilesTree";
import EditQuoteUploadRequestEmail from "./EditQuoteUploadRequestEmail";
import QuoteUploadPanel from "./QuoteUploadPanel";
import { UploadEntry } from "./upload/scanUpload";
import {
  RunKey,
  cancelRun,
  getRunSnapshot,
  retryFailed,
  runKeyFor,
  startRun,
  subscribeRun,
  useUploadSummary,
} from "./upload/uploadEngine";
import { CASHBACK_QUERY } from "modules/Clients/Services/Queries/Queries";

// What the upload form of one card holds until "Upload" is pressed
interface UploadDraft {
  entries: UploadEntry[];
  // Folder paths to create, including empty ones a drop found
  directories: string[];
}

// Folder the next upload goes into; null = card root
interface UploadTarget {
  id: number;
  name: string;
}

const EMPTY_UPLOAD_DRAFT: UploadDraft = { entries: [], directories: [] };
// Tree refetches while a run completes files: at most one per this interval, plus a final one
const UPLOAD_REFRESH_MIN_INTERVAL_MS = 5000;

/**
 * Confirm either direction of the customer upload lock.
 *
 * Both directions are worth a stop. Closing cuts the customer off from data they may not have
 * finished sending, and emails the account manager the way the portal does when the customer
 * closes their own uploads; re-opening puts data back in play that was already reported as
 * final, and notifies nobody.
 */
const ConfirmCustomerUploadModal: FC<{
  isBlocking: boolean;
  onClose: () => void;
  onApply: () => void;
  loading: boolean;
}> = ({ isBlocking, onClose, onApply, loading }) => (
  <div className="fixed inset-0 bg-transparent z-50 p-2 md:flex md:flex-row md:justify-center md:items-center">
    <div
      className="fixed inset-0 bg-black/60 -z-20"
      onClick={() => {
        if (!loading) onClose();
      }}
    />
    <div className="w-[300px] sm:w-[560px] bg-[#e4e6e9] shadow-[0_0_64px_#000] mx-auto h-fit rounded-b-[6px]">
      <h3 className="bg-[#0078AE] max-w-[600px] text-[#000] font-semibold flex justify-between py-2 px-2">
        <span className="text-[16px] leading-5">
          {isBlocking ? "Close customer uploads" : "Allow customer uploads"}
        </span>
        <span className="inline-flex justify-center items-center text-red-600">
          <i
            onClick={() => {
              if (!loading) onClose();
            }}
            className={`icon-cancel text-white ${loading ? "cursor-not-allowed" : "cursor-pointer"
              }`}
          />
        </span>
      </h3>
      <p className="font-semibold text-sm text-center p-6">
        {isBlocking
          ? "Stop the customer uploading, deleting or moving files on this quote? The account manager will be emailed."
          : "Let the customer upload and change files again? Uploading re-opens for them, and they will be able to confirm their data is final a second time."}
      </p>
      <div className="flex justify-center gap-4 p-3">
        <ActionButton
          buttonName="Cancel"
          className={`${loading ? "cursor-not-allowed" : "hover:bg-[#888] cursor-pointer"
            } bg-[#A0A0A0] border-[#A0A0A0] whitespace-nowrap`}
          buttonType="button"
          onClick={() => {
            if (!loading) onClose();
          }}
        />
        <ActionButton
          buttonName={isBlocking ? "Close uploads" : "Allow uploads"}
          buttonType="button"
          className={
            loading
              ? "bg-[#a3c69d] border-[#a3c69d] cursor-not-allowed"
              : "bg-[#87B87F] border-[#87B87F] hover:bg-[#629B58]"
          }
          onClick={() => {
            if (!loading) onApply();
          }}
          icon={isBlocking ? "icon-lock" : "icon-lock-open-alt"}
          loading={loading}
        />
      </div>
    </div>
  </div>
);

const FilesCard: FC<{
  title: string;
  iconClass: string;
  className: string;
  inputId: string;
  files: FileQuote[];
  directories: QuoteFileDirectory[];
  availableForSupplier: boolean;
  isLoading: boolean;
  canManage: boolean;
  // Separate from canManage: readonlyprocesing and logoaccount can manage folders but hold no
  // uploadFile button, and the upload mutations answer them with a bare 403.
  canUpload: boolean;
  currentFileId?: number;
  onSelectFile: (id: number) => void;
  onClearSelection: () => void;
  onRequestDelete: (id: number) => void;
  onRequestDeleteDirectory: (id: number) => void;
  onMoveFile: (fileId: number, directoryId: number | null) => void;
  onMoveDirectory: (dirId: number, parentId: number | null) => void;
  onAddDirectory: (parentId: number | null, name: string) => Promise<boolean>;
  onRenameDirectory: (id: number, currentName: string) => void;
  // Upload form state lives in the parent, next to the comment, for the same reason
  uploadDraft: UploadDraft;
  setUploadDraft: (draft: UploadDraft) => void;
  fileComment: string;
  setFileComment: (value: string) => void;
  uploadTarget: UploadTarget | null;
  onSetUploadTargetDirectory: (target: UploadTarget | null) => void;
  // Only the key goes down: QuoteUploadPanel reads the progress snapshot itself, so the
  // engine's publishes redraw that leaf and not the card
  runKey: RunKey;
  onStartUpload: () => void;
  onCancelUpload: () => void;
  onRetryFailed: () => void;
  // Why Upload is disabled whatever the form holds (the quote is not saved yet); shown as the
  // button's title
  uploadDisabledReason?: string;
  editingComment: string;
  setEditingComment: (value: string) => void;
  onSaveComment: () => void;
  isSavingComment: boolean;
  // Download the whole card tree (no arg) or a single folder's subtree (dirId).
  onDownloadZip: (directoryId?: number) => void;
  downloadingZipKey: string | null;
  // Customer upload lock — only the Data Upload Files card passes these; the PO card leaves
  // them undefined and shows nothing. Null blockedAt means the customer can still upload.
  customerUploadBlockedAt?: string | null;
  isCustomerUploadCard?: boolean;
  canToggleCustomerUpload?: boolean;
  isTogglingCustomerUpload?: boolean;
  onToggleCustomerUpload?: (isBlocked: boolean) => void;
}> = ({
  title,
  iconClass,
  className,
  inputId,
  files,
  directories,
  availableForSupplier,
  isLoading,
  canManage,
  canUpload,
  currentFileId,
  onSelectFile,
  onClearSelection,
  onRequestDelete,
  onRequestDeleteDirectory,
  onMoveFile,
  onMoveDirectory,
  onAddDirectory,
  onRenameDirectory,
  uploadDraft,
  setUploadDraft,
  fileComment,
  setFileComment,
  uploadTarget,
  onSetUploadTargetDirectory,
  runKey,
  onStartUpload,
  onCancelUpload,
  onRetryFailed,
  uploadDisabledReason,
  editingComment,
  setEditingComment,
  onSaveComment,
  isSavingComment,
  onDownloadZip,
  downloadingZipKey,
  customerUploadBlockedAt,
  isCustomerUploadCard = false,
  canToggleCustomerUpload = false,
  isTogglingCustomerUpload = false,
  onToggleCustomerUpload,
}) => {
    const isDownloadingCard =
      downloadingZipKey === `card-${availableForSupplier}`;
    // What "Download all" is about to pull: the card's file list is exactly the
    // set the server archives for this card, so its total is the archive's.
    const totalSize = useMemo(() => sumFileSizes(files), [files]);
    const isCustomerUploadBlocked = !!customerUploadBlockedAt;
    // Low-frequency selector: its identity changes only when the run starts or ends or a file
    // fails, so the card is not redrawn on every progress publish
    const uploadSummary = useUploadSummary(runKey);
    const hasUploadDraft =
      !!uploadDraft.entries.length || !!uploadDraft.directories.length;
    // Inline "new folder" input in the card header. `id` is the target parent
    // (null = root); set from the Add Folder button or a folder row's ➕.
    const [addFolderParent, setAddFolderParent] = useState<
      { id: number | null; name?: string } | undefined
    >();
    const [newFolderName, setNewFolderName] = useState("");
    const [isAddingFolder, setIsAddingFolder] = useState(false);

    // Folder open/closed state lives here rather than in the tree so the header
    // buttons can drive the whole tree. Empty = everything collapsed, which is
    // how the card opens.
    const [expandedUids, setExpandedUids] = useState<Set<string>>(new Set());
    const folderUids = useMemo(
      () => getFolderUids(directories, availableForSupplier),
      [directories, availableForSupplier],
    );
    const canExpandAll = folderUids.some((uid) => !expandedUids.has(uid));
    const canCollapseAll = folderUids.some((uid) => expandedUids.has(uid));

    const onRequestAddFolder = (parentId: number | null, parentName?: string) => {
      setAddFolderParent({ id: parentId, name: parentName });
      setNewFolderName("");
    };

    const onCancelAddFolder = () => {
      setAddFolderParent(undefined);
      setNewFolderName("");
    };

    const onSubmitAddFolder = async () => {
      if (!addFolderParent || !newFolderName.trim() || isAddingFolder) return;

      setIsAddingFolder(true);
      const isCreated = await onAddDirectory(
        addFolderParent.id,
        newFolderName.trim(),
      );
      setIsAddingFolder(false);

      if (isCreated) {
        // Open the parent so the new subfolder is actually on screen – folders
        // are collapsed by default, and ➕ works on a closed row.
        const parentId = addFolderParent.id;
        if (parentId != null) {
          setExpandedUids((prev) => {
            const next = new Set(prev);
            next.add(folderUid(parentId));
            return next;
          });
        }
        onCancelAddFolder();
      }
    };

    return (
      <div className={`${className} edit-quote__misc-card`}>
        <div className="edit-quote__misc-card-header flex items-center justify-between">
          <span>
            <i className={`${iconClass} pr-1`} />
            {title}
          </span>

          <span className="flex items-center gap-1">
            {isCustomerUploadCard && canToggleCustomerUpload && (
              <button
                type="button"
                className={`btn btn-small ${isCustomerUploadBlocked ? "btn-success" : "btn-warning"
                  }`}
                title={
                  isCustomerUploadBlocked
                    ? "Let the customer upload and change files again"
                    : "Stop the customer uploading or changing files"
                }
                disabled={isTogglingCustomerUpload}
                onClick={() =>
                  onToggleCustomerUpload &&
                  onToggleCustomerUpload(!isCustomerUploadBlocked)
                }
              >
                <i
                  className={`${isTogglingCustomerUpload
                      ? "inline-block animate-spin icon-spin4"
                      : isCustomerUploadBlocked
                        ? "icon-lock-open-alt"
                        : "icon-lock"
                    } pr-1`}
                />
                {isCustomerUploadBlocked
                  ? "Allow Customer Uploads"
                  : "Close Customer Uploads"}
              </button>
            )}

            {!!files.length && (
              <button
                type="button"
                className="btn btn-small"
                title={`Download all files as a ZIP archive (${sizeTotalTitle(
                  totalSize,
                )})`}
                disabled={isDownloadingCard}
                onClick={() => onDownloadZip()}
              >
                <i
                  className={`${isDownloadingCard
                      ? "inline-block animate-spin icon-spin4"
                      : "icon-export"
                    } pr-1`}
                />
                Download all ({formatSizeTotal(totalSize)})
              </button>
            )}
          </span>
        </div>
        <div className="edit-quote__misc-card-content">
          {/* Shown to every role that can see the card, including the read-only ones that get
            no button — knowing the data is closed is the point, not the toggle.
            Worded without attributing the act: the column is stamped both by the customer
            pressing "I have finished uploading" in the portal and by the button above, and
            the column alone does not distinguish them — only the queued email type does,
            and that is not read here. */}
          {isCustomerUploadCard && isCustomerUploadBlocked && (
            <div className="mb-2 rounded border border-[#f0c9c9] bg-[#fdf3f3] px-3 py-2 text-sm text-[#7a2f2f]">
              <i className="icon-lock pr-1" />
              Customer uploads were closed on{" "}
              {convertDateToUSFormat(customerUploadBlockedAt as string)}. They can
              no longer upload, delete or move files.
            </div>
          )}
          {isLoading && <LoadingDesign smallLoading={true} className="h-max" />}
          {!isLoading && (
            <Fragment>
              {(canManage || !!folderUids.length) && (
                <div className="mb-1 flex items-center gap-1">
                  {canManage && !addFolderParent && (
                    <button
                      className="btn btn-small btn-success"
                      onClick={() => onRequestAddFolder(null)}
                    >
                      <i className="icon-plus pr-1" />
                      Add Folder
                    </button>
                  )}
                  {!!addFolderParent && (
                    <Fragment>
                      <input
                        className="edit-quote__tree-comment-input edit-quote__add-folder-input"
                        value={newFolderName}
                        placeholder={
                          addFolderParent.name
                            ? `New folder in "${addFolderParent.name}"`
                            : "Folder name"
                        }
                        autoFocus
                        disabled={isAddingFolder}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            onSubmitAddFolder();
                          }
                          if (e.key === "Escape") {
                            onCancelAddFolder();
                          }
                        }}
                      />
                      <button
                        className="btn btn-small btn-success"
                        disabled={!newFolderName.trim() || isAddingFolder}
                        onClick={onSubmitAddFolder}
                      >
                        <i
                          className={`${isAddingFolder
                              ? "inline-block animate-spin icon-spin4"
                              : "icon-floppy"
                            } pr-1`}
                        />
                        Save
                      </button>
                      <button
                        className="btn btn-small"
                        disabled={isAddingFolder}
                        onClick={onCancelAddFolder}
                      >
                        <i className="icon-cancel pr-1" />
                        Cancel
                      </button>
                    </Fragment>
                  )}
                  {/* Whole-tree toggles – shown to every role that can see the
                      card, they only change what is on screen. */}
                  {!!folderUids.length && (
                    <span className="ml-auto flex items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-small"
                        title="Expand all folders"
                        disabled={!canExpandAll}
                        onClick={() => setExpandedUids(new Set(folderUids))}
                      >
                        <i className="icon-down-open pr-1" />
                        Expand All
                      </button>
                      <button
                        type="button"
                        className="btn btn-small"
                        title="Collapse all folders"
                        disabled={!canCollapseAll}
                        onClick={() => setExpandedUids(new Set())}
                      >
                        <i className="icon-up-open pr-1" />
                        Collapse All
                      </button>
                    </span>
                  )}
                </div>
              )}
              <QuoteFilesTree
                files={files}
                directories={directories}
                availableForSupplier={availableForSupplier}
                canManage={canManage}
                canUpload={canUpload}
                onUploadHere={(dirId, name) =>
                  onSetUploadTargetDirectory({ id: dirId, name })
                }
                currentFileId={currentFileId}
                editingComment={editingComment}
                onEditingCommentChange={setEditingComment}
                onSaveComment={onSaveComment}
                onCancelEdit={onClearSelection}
                isSavingComment={isSavingComment}
                onSelectFile={onSelectFile}
                onRequestDeleteFile={onRequestDelete}
                onRequestDeleteDirectory={onRequestDeleteDirectory}
                onMoveFile={onMoveFile}
                onMoveDirectory={onMoveDirectory}
                onAddDirectory={onRequestAddFolder}
                onRenameDirectory={onRenameDirectory}
                onDownloadDirectory={(dirId) => onDownloadZip(dirId)}
                downloadingZipKey={downloadingZipKey}
                expandedUids={expandedUids}
                setExpandedUids={setExpandedUids}
              />
            </Fragment>
          )}
          {canUpload && (
            <Fragment>
              <QuoteUploadPanel
                runKey={runKey}
                onCancel={onCancelUpload}
                onRetry={onRetryFailed}
              />
              <div>Upload New File</div>
              {!!uploadTarget && (
                <div className="mb-1 text-[13px] text-[#393939]">
                  Uploading into: <b>{uploadTarget.name}</b>
                  <button
                    type="button"
                    className="btn btn-mini ml-1"
                    title="Upload into the card root instead"
                    onClick={() => onSetUploadTargetDirectory(null)}
                  >
                    <i className="icon-cancel" />
                  </button>
                </div>
              )}
              <EditQuoteInputFiles
                inputId={inputId}
                enableFolders
                entries={uploadDraft.entries}
                directories={uploadDraft.directories}
                onEntriesChange={(entries, directories) => {
                  setUploadDraft({ entries, directories });
                }}
                fileComment={{
                  value: fileComment,
                  onChange: (value) => {
                    setFileComment(value);
                  },
                }}
              />
              <div className="btn-group">
                <button
                  className="btn btn-small btn-success"
                  disabled={
                    !hasUploadDraft ||
                    uploadSummary.active ||
                    !!uploadDisabledReason
                  }
                  title={
                    uploadDisabledReason ||
                    (uploadSummary.active
                      ? "An upload is already running for this card"
                      : undefined)
                  }
                  onClick={() => {
                    onStartUpload();
                  }}
                >
                  <i
                    className={`${uploadSummary.active
                        ? "inline-block animate-spin icon-spin4"
                        : "icon-upload"
                      } pr-1`}
                  />
                  Upload
                </button>
              </div>
            </Fragment>
          )}
        </div>
      </div>
    );
  };

const EditQuoteMisc: FC<{
  quoteEditData: QuoteEditData;
  setQuoteEditData: React.Dispatch<React.SetStateAction<QuoteEditData>>;
  onUpdateQuotePartial: (data: Partial<QuoteEditData>) => Promise<void>;
  accessModules: any;
  tabMiscAccess: any;
  userId?: number;
  hasEditAndDeleteAccess: boolean;
  roleName?: string;
}> = ({
  quoteEditData,
  setQuoteEditData,
  onUpdateQuotePartial,
  accessModules,
  tabMiscAccess,
  userId,
  hasEditAndDeleteAccess,
  roleName,
}) => {
    const accessForm = useMemo(() => tabMiscAccess?.forms, [tabMiscAccess]);
    const accessHeader = useMemo(() => tabMiscAccess?.header, [tabMiscAccess]);

    const accessHeaderFields = useMemo(
      () => accessHeader?.fields,
      [accessHeader],
    );
    const accessHeaderButtons = useMemo(
      () => accessHeader?.buttons,
      [accessHeader],
    );

    const accessHeaderFieldsSupplier = useMemo(
      () => accessHeaderFields?.supplier,
      [accessHeaderFields],
    );
    const accessHeaderFieldsRequiredDeliveryDate = useMemo(
      () => accessHeaderFields?.requiredDeliveryDate,
      [accessHeaderFields],
    );
    const accessHeaderFieldsUseInventory = useMemo(
      () => accessHeaderFields?.useInventory,
      [accessHeaderFields],
    );

    const accessFormEditExpenses = useMemo(
      () => accessForm?.editExpenses,
      [accessForm],
    );
    const accessFormOrderCredit = useMemo(
      () => accessForm?.orderCredits,
      [accessForm],
    );
    const accessFormComments = useMemo(() => accessForm?.comments, [accessForm]);
    const accessFormMessage = useMemo(
      () => accessForm?.messageBoard,
      [accessForm],
    );
    const accessFormFiles = useMemo(() => accessForm?.files, [accessForm]);
    const accessFormSupplierFiles = useMemo(
      () => accessForm?.supplierFiles,
      [accessForm],
    );
    const isSupplierReadOnly = useMemo(() => roleName === "supplier", [roleName]);

    // The ACL button key is the real gate. hasEditAndDeleteAccess alone is not enough:
    // readonlyprocesing passes it and has a supplierFiles node, so it would get an enabled
    // button whose mutation is limited to admin/sales/procesing and answers with a bare 403.
    const canToggleCustomerUpload = useMemo(
      () =>
        hasEditAndDeleteAccess &&
        !isSupplierReadOnly &&
        !!accessFormSupplierFiles?.buttons?.includes("toggleCustomerUploadBlock"),
      [hasEditAndDeleteAccess, isSupplierReadOnly, accessFormSupplierFiles],
    );

    // The same key gates the upload form and the tree's "Upload into this folder": the
    // uploadFile button is where the roles differ (readonlyprocesing and logoaccount pass
    // hasEditAndDeleteAccess but have no buttons at all), and the upload mutations are limited
    // to admin/sales/procesing.
    const canUploadFiles = useMemo(
      () =>
        hasEditAndDeleteAccess &&
        !isSupplierReadOnly &&
        !!accessFormFiles?.buttons?.includes("uploadFile"),
      [hasEditAndDeleteAccess, isSupplierReadOnly, accessFormFiles],
    );
    const canUploadSupplierFiles = useMemo(
      () =>
        hasEditAndDeleteAccess &&
        !isSupplierReadOnly &&
        !!accessFormSupplierFiles?.buttons?.includes("uploadFile"),
      [hasEditAndDeleteAccess, isSupplierReadOnly, accessFormSupplierFiles],
    );

    const accessFieldsExpense = useMemo(
      () => accessFormEditExpenses?.fields,
      [accessFormEditExpenses],
    );
    const accessFieldsSelectExpense = useMemo(
      () =>
        accessFieldsExpense?.expense && accessFieldsExpense?.expense?.editable,
      [accessFieldsExpense],
    );
    const accessFieldsValueExpense = useMemo(
      () => accessFieldsExpense?.value && accessFieldsExpense?.value?.editable,
      [accessFieldsExpense],
    );
    const accessButtonsExpense = useMemo(
      () => accessFormEditExpenses?.buttons,
      [accessFormEditExpenses],
    );

    const accessFormFieldsValueComments = useMemo(
      () =>
        accessFormComments?.fields?.text &&
        accessFormComments?.fields?.text?.editable,
      [accessFormComments],
    );
    const accessButtonsComments = useMemo(
      () => accessFormComments?.buttons,
      [accessFormComments],
    );

    const accessFormFieldsValueMessages = useMemo(
      () =>
        accessFormMessage?.fields?.text &&
        accessFormMessage?.fields?.text?.editable,
      [accessFormMessage],
    );
    const accessButtonsMessages = useMemo(
      () => accessFormMessage?.buttons,
      [accessFormMessage],
    );

    const queryMiscByRole = useMemo(() => {
      if (roleName === "supplier") {
        return QUOTE_MISC_QUERY_SUPPLIER;
      }

      return QUOTE_MISC_QUERY;
    }, [roleName]);

    const [fetchCashback] = useLazyQuery(CASHBACK_QUERY, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [updateMiscInfoByQuote] = useLazyQuery(queryMiscByRole, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [requestFilesZipLink] = useLazyQuery(QUOTE_FILES_ZIP_LINK, {
      fetchPolicy: "network-only",
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    // Light refetch of the two file cards while a direct-to-S3 run completes files. Separate
    // from updateMiscInfoByQuote on purpose: that one swaps every card for a LoadingDesign and
    // pulls expenses, credits, comments and messages along each time.
    const [refreshQuoteFiles] = useLazyQuery(QUOTE_FILES_REFRESH_QUERY, {
      fetchPolicy: "network-only",
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    // Which ZIP is downloading: `card-<availableForSupplier>` or `dir-<id>`.
    const [downloadingZipKey, setDownloadingZipKey] = useState<string | null>(
      null,
    );

    const onDownloadZip = useCallback(
      async (availableForSupplier: boolean, directoryId?: number) => {
        const key =
          directoryId != null
            ? `dir-${directoryId}`
            : `card-${availableForSupplier}`;
        setDownloadingZipKey(key);

        await requestFilesZipLink({
          variables: {
            quoteId: quoteEditData.id,
            availableForSupplier,
            directoryId: directoryId ?? null,
          },
        })
          .then((res) => {
            const zip = res?.data?.quoteFilesZipLink;

            // A failed archive used to leave the spinner stopping and nothing
            // else — indistinguishable from a download the browser swallowed.
            if (!zip?.url) {
              if (!res?.error) {
                setNotification([
                  {
                    type: "ERROR",
                    message: "Could not prepare the archive. Please try again.",
                  },
                ]);
              }
              return;
            }

            if (!zip.fileCount) {
              setNotification([
                {
                  type: "ERROR",
                  message: "There are no files to download here.",
                },
              ]);
              return;
            }

            // Navigating to the URL hands the transfer to the browser, which
            // writes the archive to disk as it arrives and shows its own
            // progress. Nothing about the response passes through this tab's
            // memory, so archive size stops being a limit here too.
            const link = document.createElement("a");
            link.href = zip.url;
            link.download = zip.fileName;
            link.click();

            // The server cannot report a missing file once the download has
            // started - the response is already a ZIP by then - so unreadable
            // files are listed in a _MISSING_FILES.txt entry inside the archive.
            setNotification([
              {
                type: "SUCCESS",
                message: `Preparing ${zip.fileName} (${zip.fileCount} file${zip.fileCount > 1 ? "s" : ""
                  }, ${zip.totalSize}). The download will start shortly; large archives take a while.`,
              },
            ]);
          })
          .finally(() => {
            setDownloadingZipKey(null);
          });
      },
      [requestFilesZipLink, quoteEditData.id],
    );

    const [currentExpensesId, setCurrentExpensesId] = useState<number | null>(
      null,
    );
    const [currentFileId, setCurrentFileId] = useState<number | undefined>();
    const [currentSupplierFileId, setCurrentSupplierFileId] = useState<
      number | undefined
    >();
    const [isLoadingUpdateExpense, setIsLoadingUpdateExpense] = useState(false);
    const [isLoadingUpdateComment, setIsLoadingUpdateComment] = useState(false);
    const [isLoadingUpdateMessage, setIsLoadingUpdateMessage] = useState(false);

    const [expensesBuQuoteList, setExpensesByQuoteList] = useState<
      {
        id: number;
        expensesId: number;
        expenseName: string;
        value: number;
      }[]
    >([]);
    const [creditByQuoteList, setCreditByQuoteList] = useState<CreditOrder[]>([]);
    const [commentsByQuoteList, setCommentsByQuoteList] = useState<Comments[]>(
      [],
    );
    const [messageByQuoteList, setMessageByQuoteList] = useState<Comments[]>([]);
    const [filesByQuoteList, setFilesByQuoteList] = useState<FileQuote[]>([]);
    const [supplierFilesByQuoteList, setSupplierFilesByQuoteList] = useState<
      FileQuote[]
    >([]);
    const [directoriesByQuoteList, setDirectoriesByQuoteList] = useState<
      QuoteFileDirectory[]
    >([]);

    const getCashback = useCallback(async () => {
      if (!quoteEditData?.client?.id) return;

      setIsLoadingSaveCashback(true);
      setCashback(0);

      await fetchCashback({ variables: { id: quoteEditData?.client?.id } })
        .then(async (res) => {
          const { data } = res;

          if (!data) return;

          const { cashbackQuery } = data;
          setCashback(cashbackQuery ?? 0);
        })
        .finally(() => {
          setIsLoadingSaveCashback(false);
        });
    }, [fetchCashback, quoteEditData?.client?.id]);

    useEffect(() => {
      getCashback();
    }, [quoteEditData?.client?.id]);

    const clearExpenses = useMemo(
      (): { expensesId: number; value: number | string } => ({
        expensesId: 1,
        value: "",
      }),
      [],
    );

    const [currentExpense, setCurrentExpense] = useState(clearExpenses);
    const [cashback, setCashback] = useState<number>(0);

    const clearEditDataExpense = useCallback(() => {
      if (currentExpensesId) {
        setCurrentExpensesId(null);
      }

      setCurrentExpense(clearExpenses);
    }, [currentExpensesId]);

    useEffect(() => {
      if (!currentExpensesId) {
        clearEditDataExpense();
        return;
      }

      const searchExpense = expensesBuQuoteList.find(
        (el) => el.id === currentExpensesId,
      );

      if (searchExpense) {
        setCurrentExpense({
          expensesId: searchExpense.expensesId,
          value: searchExpense.value,
        });
      }
    }, [currentExpensesId]);

    const [createQuoteComment] = useMutation(CREATE_QUOTE_COMMENT, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [createQuoteMessage] = useMutation(CREATE_QUOTE_MESSAGE, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [createQuoteExpense] = useMutation(CREATE_QUOTE_EXPENSE, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });
    const [updateQuoteExpense] = useMutation(UPDATE_QUOTE_EXPENSE, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [updateCashback] = useMutation(UPDATE_CASHBACK, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [deleteQuoteExpense] = useMutation(DELETE_QUOTE_EXPENSE, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [deleteQuoteFile] = useMutation(DELETE_QUOTE_FILES, {
      onError: (error) => {
        setNotification([
          {
            type: "ERROR",
            message: `${error?.message}`,
          },
        ]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [updateQuoteFile] = useMutation(UPDATE_QUOTE_FILES, {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [createQuoteFileDirectory] = useMutation(CREATE_QUOTE_FILE_DIRECTORY, {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [updateQuoteFileDirectory] = useMutation(UPDATE_QUOTE_FILE_DIRECTORY, {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    const [deleteQuoteFileDirectory] = useMutation(DELETE_QUOTE_FILE_DIRECTORY, {
      onError: (error) => {
        setNotification([{ type: "ERROR", message: `${error?.message}` }]);
      },
      notifyOnNetworkStatusChange: true,
    });

    // No onCompleted refetch flag: the mutation hands the new value straight back and
    // setQuoteEditData is right here. Routing this through onUpdateQuotePartial instead would
    // drag requiredDeliveryDate and followUpDate along on every toggle.
    const [setQuoteCustomerUploadBlock] = useMutation(
      SET_QUOTE_CUSTOMER_UPLOAD_BLOCK,
      { notifyOnNetworkStatusChange: true },
    );

    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [deleteId, setDeleteId] = useState<
      | {
        type: string;
        id: number;
      }
      | undefined
    >();
    // undefined = no dialog open; the boolean is the direction being confirmed
    const [customerUploadBlockPending, setCustomerUploadBlockPending] = useState<
      boolean | undefined
    >();
    const [isTogglingCustomerUpload, setIsTogglingCustomerUpload] =
      useState(false);
    const [commentText, setCommentText] = useState("");
    const [messageText, setMessageText] = useState("");
    const [fileComment, setFileComment] = useState("");
    const [uploadDraft, setUploadDraft] =
      useState<UploadDraft>(EMPTY_UPLOAD_DRAFT);
    const [uploadTarget, setUploadTarget] = useState<UploadTarget | null>(null);
    const [supplierFileComment, setSupplierFileComment] = useState("");
    const [supplierUploadDraft, setSupplierUploadDraft] =
      useState<UploadDraft>(EMPTY_UPLOAD_DRAFT);
    const [supplierUploadTarget, setSupplierUploadTarget] =
      useState<UploadTarget | null>(null);
    // Inline comment editing on a tree row – independent from the upload form.
    const [editFileComment, setEditFileComment] = useState("");
    const [editSupplierFileComment, setEditSupplierFileComment] = useState("");
    const [isSavingFileComment, setIsSavingFileComment] = useState(false);
    const [isSavingSupplierFileComment, setIsSavingSupplierFileComment] =
      useState(false);

    const onCreateComment = useCallback(async () => {
      setIsLoadingUpdateComment(true);

      await createQuoteComment({
        variables: {
          quoteId: quoteEditData.id,
          userId,
          text: commentText,
        },
      })
        .then((res) => {
          const { data } = res;

          if (!data) return;

          if (data.createQuoteComment?.id) {
            setIsNeedUpdateMiscInfoByQuote(true);

            setNotification([
              {
                type: "SUCCESS",
                message: "Comment successfully sent",
              },
            ]);
          }
        })
        .finally(() => {
          setIsLoadingUpdateComment(false);
        });
    }, [commentText, quoteEditData]);

    // The Misc tab can be drawn before the quote has an id (EditQuoteUploadRequestEmail guards
    // the same way): without one there is nothing to attach the files to
    const quoteId = quoteEditData.id ?? 0;
    const poRunKey = useMemo(() => runKeyFor(quoteId, false), [quoteId]);
    const supplierRunKey = useMemo(() => runKeyFor(quoteId, true), [quoteId]);
    // The button says why it does nothing rather than being enabled and doing nothing
    const uploadDisabledReason = quoteId
      ? undefined
      : "Save the quote before uploading files";

    // Hands the form to the engine and clears it right away: from here on the run lives in
    // the module store, and a second click cannot send the same files twice.
    const onCreateFiles = useCallback(() => {
      if (!quoteId) return;
      startRun({
        quoteId,
        availableForSupplier: false,
        comment: fileComment,
        targetDirectoryId: uploadTarget ? uploadTarget.id : null,
        entries: uploadDraft.entries,
        directories: uploadDraft.directories,
      });
      setUploadDraft(EMPTY_UPLOAD_DRAFT);
      setFileComment("");
    }, [quoteId, fileComment, uploadTarget, uploadDraft]);

    const onCreateSupplierFiles = useCallback(() => {
      if (!quoteId) return;
      startRun({
        quoteId,
        availableForSupplier: true,
        comment: supplierFileComment,
        targetDirectoryId: supplierUploadTarget ? supplierUploadTarget.id : null,
        entries: supplierUploadDraft.entries,
        directories: supplierUploadDraft.directories,
      });
      setSupplierUploadDraft(EMPTY_UPLOAD_DRAFT);
      setSupplierFileComment("");
    }, [quoteId, supplierFileComment, supplierUploadTarget, supplierUploadDraft]);

    // A target folder deleted, or moved to the other card, since it was picked would fail
    // every init of the next upload: fall back to the card root instead
    useEffect(() => {
      const stillThere = (
        target: UploadTarget | null,
        availableForSupplier: boolean,
      ) =>
        !target ||
        directoriesByQuoteList.some(
          (dir) =>
            dir.id === target.id &&
            !!dir.availableForSupplier === availableForSupplier,
        );
      if (!stillThere(uploadTarget, false)) setUploadTarget(null);
      if (!stillThere(supplierUploadTarget, true)) setSupplierUploadTarget(null);
    }, [directoriesByQuoteList, uploadTarget, supplierUploadTarget]);

    const onMoveFile = useCallback(
      async (fileId: number, directoryId: number | null) => {
        await updateQuoteFile({
          variables: { id: fileId, directoryId },
        }).then((res) => {
          if (res?.data?.updateQuoteFile) {
            setIsNeedUpdateMiscInfoByQuote(true);
          }
        });
      },
      [updateQuoteFile],
    );

    const onMoveDirectory = useCallback(
      async (dirId: number, parentId: number | null) => {
        await updateQuoteFileDirectory({
          variables: { id: dirId, parentId },
        }).then((res) => {
          if (res?.data?.updateQuoteFileDirectory) {
            setIsNeedUpdateMiscInfoByQuote(true);
          }
        });
      },
      [updateQuoteFileDirectory],
    );

    const onAddDirectory = useCallback(
      async (
        parentId: number | null,
        availableForSupplier: boolean,
        name: string,
      ): Promise<boolean> => {
        if (!name.trim()) return false;

        const res = await createQuoteFileDirectory({
          variables: {
            quoteId: quoteEditData.id,
            name: name.trim(),
            parentId,
            availableForSupplier,
            userId,
          },
        });

        if (res?.data?.createQuoteFileDirectory) {
          setIsNeedUpdateMiscInfoByQuote(true);
          return true;
        }
        return false;
      },
      [createQuoteFileDirectory, quoteEditData, userId],
    );

    const onRenameDirectory = useCallback(
      async (id: number, currentName: string) => {
        const name = window.prompt("Folder name", currentName);
        if (!name || name === currentName) return;

        await updateQuoteFileDirectory({
          variables: { id, name },
        }).then((res) => {
          if (res?.data?.updateQuoteFileDirectory) {
            setIsNeedUpdateMiscInfoByQuote(true);
          }
        });
      },
      [updateQuoteFileDirectory],
    );

    const onCreateMessage = useCallback(async () => {
      setIsLoadingUpdateMessage(true);

      await createQuoteMessage({
        variables: {
          quoteId: quoteEditData.id,
          userId,
          text: messageText,
        },
      })
        .then((res) => {
          const { data } = res;

          if (!data) return;

          if (data.createQuoteMessage?.id) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setMessageText("");
            setNotification([
              {
                type: "SUCCESS",
                message: "Message successfully sent",
              },
            ]);
          }
        })
        .finally(() => {
          setIsLoadingUpdateMessage(false);
        });
    }, [messageText, quoteEditData]);

    const onDelete = useCallback(async () => {
      if (!deleteId) return;
      const onDeleteHandler = async () => {
        if (deleteId.type === "expense") {
          return await deleteQuoteExpense({
            variables: {
              ids: [deleteId.id],
            },
          });
        }

        if (deleteId.type === "file") {
          return await deleteQuoteFile({
            variables: {
              ids: [deleteId.id],
            },
          });
        }

        if (deleteId.type === "directory") {
          return await deleteQuoteFileDirectory({
            variables: {
              ids: [deleteId.id],
            },
          });
        }
      };

      setIsLoadingDelete(true);

      await onDeleteHandler()
        .then((res) => {
          if (!res) return;

          const { data } = res;

          if (!data) return;

          const {
            deleteQuoteExpense,
            deleteQuoteFile,
            deleteQuoteFileDirectory: deletedDirectoryResult,
          } = data;

          if (deleteId.type === "expense" && deleteId.id === currentExpensesId) {
            setCurrentExpensesId(null);
          }

          if (deleteId.type === "expense" && deleteQuoteExpense) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              {
                type: "SUCCESS",
                message: `${capitalize(deleteId.type)} deleted successfully`,
              },
            ]);
          }

          if (deleteId.type === "file" && deleteId.id === currentFileId) {
            setCurrentExpensesId(null);
          }

          if (deleteId.type === "file" && deleteQuoteFile) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              {
                type: "SUCCESS",
                message: `${capitalize(deleteId.type)} deleted successfully`,
              },
            ]);
          }

          if (deleteId.type === "directory" && deletedDirectoryResult) {
            if (deleteId.id === currentFileId) {
              setCurrentFileId(undefined);
            }
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              {
                type: "SUCCESS",
                message: "Folder deleted successfully",
              },
            ]);
          }
        })
        .finally(() => {
          setIsLoadingDelete(false);
          setDeleteId(undefined);
        });
    }, [deleteId]);

    const [isNeedUpdateMiscInfoByQuote, setIsNeedUpdateMiscInfoByQuote] =
      useState(false);

    // Coordination between the heavy misc refetch and the light file-tree refetch the upload
    // engine drives: only one response at a time may land, or an older one could overwrite
    // newer lists. Refs, not state: none of this should redraw anything by itself.
    const isLoadingMiscRef = useRef(false);
    const pendingFilesRefreshRef = useRef(false);
    const refreshFilesTreeRef = useRef<() => void>(() => undefined);

    const onSaveCashback = useCallback(async () => {
      setIsLoadingSaveCashback(true);
      await updateCashback({
        variables: {
          id: quoteEditData?.client?.id,
          cashbackAmount: cashback,
        },
      }).finally(() => {
        setIsLoadingSaveCashback(false);
      });
    }, [cashback, quoteEditData?.client?.id, updateCashback]);

    const onSaveExpense = useCallback(async () => {
      const onSave = async () => {
        if (currentExpensesId) {
          return await updateQuoteExpense({
            variables: {
              id: currentExpensesId,
              quoteId: quoteEditData.id,
              expenseId: currentExpense.expensesId,
              value: currentExpense.value,
            },
          });
        }

        return await createQuoteExpense({
          variables: {
            quoteId: quoteEditData.id,
            expenseId: currentExpense.expensesId,
            value: currentExpense.value,
          },
        });
      };

      setIsLoadingUpdateExpense(true);

      await onSave()
        .then((res) => {
          const { data } = res;
          if (!data) return;

          const { updateQuoteExpense, createQuoteExpense } = data;

          if (
            (!!currentExpensesId && updateQuoteExpense.id) ||
            (!currentExpensesId && createQuoteExpense.id)
          ) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              {
                type: "SUCCESS",
                message: `Expense successfully ${currentExpensesId ? "updated" : "created"
                  }`,
              },
            ]);
          }
        })
        .finally(() => {
          setIsLoadingUpdateExpense(false);
        });
    }, [currentExpense, currentExpensesId, quoteEditData]);

    const onApplyCustomerUploadBlock = useCallback(async () => {
      if (customerUploadBlockPending === undefined) return;

      const isBlocked = customerUploadBlockPending;
      setIsTogglingCustomerUpload(true);

      await setQuoteCustomerUploadBlock({
        variables: { quoteId: quoteEditData.id, isBlocked },
      })
        .then(({ data }) => {
          setQuoteEditData((prevState) => ({
            ...prevState,
            customerUploadBlockedAt:
              data?.setQuoteCustomerUploadBlock?.customerUploadBlockedAt ?? null,
          }));
          setNotification([
            {
              type: "SUCCESS",
              message: isBlocked
                ? "Customer uploading has been closed for this quote"
                : "Customer uploading has been re-opened for this quote",
            },
          ]);
          setCustomerUploadBlockPending(undefined);
        })
        .catch(catchNotification)
        .finally(() => {
          setIsTogglingCustomerUpload(false);
        });
    }, [
      customerUploadBlockPending,
      quoteEditData.id,
      setQuoteCustomerUploadBlock,
      setQuoteEditData,
    ]);

    const onUpdateQuote = useCallback(async () => {
      setIsLoadingUpdatedQuote(true);

      await onUpdateQuotePartial({
        supplierId: quoteEditData.supplierId || null,
        useInventory: quoteEditData.useInventory,
        requiredDeliveryDate: quoteEditData.requiredDeliveryDate || null,
        followUpDate: quoteEditData.followUpDate || null,
      }).finally(() => {
        setIsLoadingUpdatedQuote(false);
      });
    }, [quoteEditData]);

    const onUpdateMiscInfoByQuote = useCallback(async () => {
      setIsLoadingUpdateMiscInfoByQuote(true);
      isLoadingMiscRef.current = true;

      await updateMiscInfoByQuote({
        variables: {
          quoteId: quoteEditData.id,
        },
      })
        .then((res) => {
          const { data } = res;

          if (!data) return;

          const {
            quoteCommentsByQuote,
            quoteCreditsByQuote,
            quoteExpensesByQuote,
            quoteFilesByQuote,
            quoteSupplierFilesByQuote,
            quoteMessagesByQuote,
            quoteFileDirectoriesByQuote,
          } = data;
          if (quoteExpensesByQuote) {
            setExpensesByQuoteList(
              quoteExpensesByQuote.length
                ? quoteExpensesByQuote.map((expense: QuoteExpenses) => ({
                  id: expense.id,
                  expensesId: expense.expense.id,
                  expenseName: expense.expense.name,
                  value: expense.value,
                }))
                : [],
            );
          }

          if (quoteCreditsByQuote) {
            setCreditByQuoteList(
              quoteCreditsByQuote.length ? quoteCreditsByQuote : [],
            );
          }

          if (quoteCommentsByQuote) {
            setCommentsByQuoteList(
              quoteCommentsByQuote.length ? quoteCommentsByQuote : [],
            );
          }

          if (quoteMessagesByQuote) {
            setMessageByQuoteList(
              quoteMessagesByQuote.length ? quoteMessagesByQuote : [],
            );
          }

          if (quoteFilesByQuote) {
            setFilesByQuoteList(
              quoteFilesByQuote.length ? quoteFilesByQuote : [],
            );
          }

          if (quoteSupplierFilesByQuote) {
            setSupplierFilesByQuoteList(
              quoteSupplierFilesByQuote.length ? quoteSupplierFilesByQuote : [],
            );
          }

          if (quoteFileDirectoriesByQuote) {
            setDirectoriesByQuoteList(
              quoteFileDirectoriesByQuote.length
                ? quoteFileDirectoriesByQuote
                : [],
            );
          }
        })
        .finally(() => {
          setIsLoadingUpdateMiscInfoByQuote(false);
          isLoadingMiscRef.current = false;

          if (isNeedUpdateMiscInfoByQuote) {
            setIsNeedUpdateMiscInfoByQuote(false);
          }
          // A light tree refetch that came due meanwhile goes out now, behind this response,
          // so the newer file lists are the ones that stay on screen
          if (pendingFilesRefreshRef.current) {
            pendingFilesRefreshRef.current = false;
            refreshFilesTreeRef.current();
          }
        });
    }, [quoteEditData, isNeedUpdateMiscInfoByQuote]);

    const [isLoadingUpdateMiscInfoByQuote, setIsLoadingUpdateMiscInfoByQuote] =
      useState(false);
    const [isLoadingUpdatedQuote, setIsLoadingUpdatedQuote] = useState(false);
    const [isLoadingSaveCashback, setIsLoadingSaveCashback] = useState(false);

    const isRefreshingFilesRef = useRef(false);
    const refreshFilesTree = useCallback(async () => {
      if (isRefreshingFilesRef.current || isLoadingMiscRef.current) {
        pendingFilesRefreshRef.current = true;
        return;
      }
      isRefreshingFilesRef.current = true;

      await refreshQuoteFiles({
        variables: {
          quoteId: quoteEditData.id,
        },
      })
        .then((res) => {
          const { data } = res;

          if (!data) return;

          const {
            quoteFilesByQuote,
            quoteSupplierFilesByQuote,
            quoteFileDirectoriesByQuote,
          } = data;

          if (quoteFilesByQuote) {
            setFilesByQuoteList(quoteFilesByQuote);
          }

          if (quoteSupplierFilesByQuote) {
            setSupplierFilesByQuoteList(quoteSupplierFilesByQuote);
          }

          if (quoteFileDirectoriesByQuote) {
            setDirectoriesByQuoteList(quoteFileDirectoriesByQuote);
          }
        })
        .finally(() => {
          isRefreshingFilesRef.current = false;

          if (pendingFilesRefreshRef.current) {
            pendingFilesRefreshRef.current = false;
            refreshFilesTreeRef.current();
          }
        });
    }, [refreshQuoteFiles, quoteEditData.id]);
    refreshFilesTreeRef.current = refreshFilesTree;

    // Refetch the trees as the engine completes files: at most once per 5 s while a run is
    // going, at once when it ends, and never dropped when another refetch is in flight.
    // Subscribed by hand rather than through useUploadRun: that hook would redraw this whole
    // tab on every progress publish, and only completedVersion and state matter here.
    const appliedCompletedVersionRef = useRef<{ [key: string]: number }>({});
    const lastFilesRefreshAtRef = useRef(0);
    const filesRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    useEffect(() => {
      const keys = [poRunKey, supplierRunKey];
      const applied = appliedCompletedVersionRef.current;
      // Whatever completed before this mount is covered by the misc fetch the mount issues
      keys.forEach((key) => {
        const snapshot = getRunSnapshot(key);
        applied[key] = snapshot ? snapshot.completedVersion : 0;
      });

      const fire = () => {
        filesRefreshTimerRef.current = null;
        lastFilesRefreshAtRef.current = Date.now();
        refreshFilesTreeRef.current();
      };

      const check = () => {
        let due = false;
        let final = false;
        keys.forEach((key) => {
          const snapshot = getRunSnapshot(key);
          if (!snapshot || snapshot.completedVersion === applied[key]) return;
          // A smaller version means a new run replaced the old one under this key
          if (snapshot.completedVersion > applied[key]) {
            due = true;
            if (snapshot.state === "done") final = true;
          }
          applied[key] = snapshot.completedVersion;
        });
        if (!due) return;

        if (final) {
          // The last refetch waits for nothing: the tree has to be complete when the bar is
          if (filesRefreshTimerRef.current !== null) {
            clearTimeout(filesRefreshTimerRef.current);
          }
          fire();
          return;
        }
        if (filesRefreshTimerRef.current !== null) return;
        const wait = Math.max(
          0,
          lastFilesRefreshAtRef.current +
            UPLOAD_REFRESH_MIN_INTERVAL_MS -
            Date.now(),
        );
        filesRefreshTimerRef.current = setTimeout(fire, wait);
      };

      const unsubscribes = keys.map((key) => subscribeRun(key, check));
      return () => {
        unsubscribes.forEach((unsubscribe) => unsubscribe());
        if (filesRefreshTimerRef.current !== null) {
          clearTimeout(filesRefreshTimerRef.current);
          filesRefreshTimerRef.current = null;
        }
      };
    }, [poRunKey, supplierRunKey]);
    const useInventory = useMemo(
      () => quoteEditData?.useInventory,
      [quoteEditData],
    );
    const supplierId = useMemo(() => quoteEditData?.supplierId, [quoteEditData]);

    const requiredDeliveryDate = useMemo(
      () =>
        quoteEditData?.requiredDeliveryDate
          ? new Date(quoteEditData?.requiredDeliveryDate)
          : null,
      [quoteEditData],
    );

    const [prevFetchSettings, setPrevFetchSettings] = useState<Params>({});

    useEffect(() => {
      const currentParams = {
        id: quoteEditData.id,
      };

      onChangeParamsCallback(
        prevFetchSettings,
        currentParams,
        (newParams) => setPrevFetchSettings(newParams),
        () => {
          setIsNeedUpdateMiscInfoByQuote(true);
        },
      );
    }, [quoteEditData.id, prevFetchSettings]);

    useEffect(() => {
      if (isNeedUpdateMiscInfoByQuote) {
        onUpdateMiscInfoByQuote();
      }
    }, [isNeedUpdateMiscInfoByQuote]);

    const onSetCurrentFileId = useCallback(
      (id: number) => {
        const file = filesByQuoteList.find((item) => item.id === id);
        setEditFileComment(file?.comment || "");
        setCurrentFileId(id);
      },
      [filesByQuoteList],
    );

    const onSetCurrentSupplierFileId = useCallback(
      (id: number) => {
        const file = supplierFilesByQuoteList.find((item) => item.id === id);
        setEditSupplierFileComment(file?.comment || "");
        setCurrentSupplierFileId(id);
      },
      [supplierFilesByQuoteList],
    );

    const onUpdateFileComment = useCallback(async () => {
      if (!currentFileId) return;

      const file = filesByQuoteList.find((item) => item.id === currentFileId);
      setIsSavingFileComment(true);

      await updateQuoteFile({
        variables: {
          id: currentFileId,
          comment: editFileComment,
          directoryId: file?.directoryId ?? null,
        },
      })
        .then((res) => {
          if (res?.data?.updateQuoteFile) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              { type: "SUCCESS", message: "Comment updated successfully" },
            ]);
            setCurrentFileId(undefined);
            setEditFileComment("");
          }
        })
        .finally(() => {
          setIsSavingFileComment(false);
        });
    }, [currentFileId, editFileComment, filesByQuoteList, updateQuoteFile]);

    const onUpdateSupplierFileComment = useCallback(async () => {
      if (!currentSupplierFileId) return;

      const file = supplierFilesByQuoteList.find(
        (item) => item.id === currentSupplierFileId,
      );
      setIsSavingSupplierFileComment(true);

      await updateQuoteFile({
        variables: {
          id: currentSupplierFileId,
          comment: editSupplierFileComment,
          directoryId: file?.directoryId ?? null,
        },
      })
        .then((res) => {
          if (res?.data?.updateQuoteFile) {
            setIsNeedUpdateMiscInfoByQuote(true);
            setNotification([
              { type: "SUCCESS", message: "Comment updated successfully" },
            ]);
            setCurrentSupplierFileId(undefined);
            setEditSupplierFileComment("");
          }
        })
        .finally(() => {
          setIsSavingSupplierFileComment(false);
        });
    }, [
      currentSupplierFileId,
      editSupplierFileComment,
      supplierFilesByQuoteList,
      updateQuoteFile,
    ]);
    return (
      <section>
        {!!accessHeader && (
          <div className="row-fluid edit-quote__centered-items justify-start">
            {!!accessHeaderFieldsSupplier && (
              <SelectFromServerData
                value={supplierId ?? 0}
                onChange={(value) => {
                  setQuoteEditData((prevState) => {
                    const tmpEditData = cloneDeep(prevState);

                    let currentValue = value;

                    if (typeof currentValue === "string") {
                      currentValue = +currentValue;
                    }

                    tmpEditData.supplierId = currentValue || null;

                    return tmpEditData;
                  });
                }}
                type="suppliers"
                className="box-max"
                label="Supplier"
                selectWidth="200px"
                disabled={!accessHeaderFieldsSupplier.editable}
              />
            )}
            {!!accessHeaderFieldsRequiredDeliveryDate && (
              <Calendar
                label="Required Delivery Date:"
                className="box-max"
                name="required-delievery-date"
                onStartDateChange={(value) => {
                  setQuoteEditData((prevState) => {
                    const tmpEditData = cloneDeep(prevState);

                    tmpEditData.requiredDeliveryDate = value;

                    return tmpEditData;
                  });
                }}
                type="default"
                startDate={quoteEditData.requiredDeliveryDate}
                inputWidth="200px"
                disabled={!accessHeaderFieldsRequiredDeliveryDate.editable}
              />
            )}
            {roleName && roleName !== "supplier" && (
              <>
                <Calendar
                  label="Follow Up Reminder Date:"
                  className="box-max"
                  name="follow-up-reminder-date"
                  onStartDateChange={(value) => {
                    setQuoteEditData((prevState) => {
                      const tmpEditData = cloneDeep(prevState);

                      tmpEditData.followUpDate = value;

                      return tmpEditData;
                    });
                  }}
                  type="default"
                  startDate={quoteEditData.followUpDate}
                  inputWidth="200px"
                  disabled={!hasEditAndDeleteAccess}
                />
                <button
                  type="button"
                  className="btn btn-small"
                  disabled={
                    !hasEditAndDeleteAccess || !quoteEditData.followUpDate
                  }
                  onClick={() => {
                    setQuoteEditData((prevState) => {
                      const tmpEditData = cloneDeep(prevState);

                      tmpEditData.followUpDate = null;

                      return tmpEditData;
                    });
                  }}
                >
                  Reset Follow Up
                </button>
              </>
            )}
            {!!accessHeaderFieldsUseInventory && (
              <CheckboxNew
                value={useInventory}
                onChange={(value) => {
                  setQuoteEditData((prevState) => {
                    const tmpEditData = cloneDeep(prevState);

                    tmpEditData.useInventory = value;

                    return tmpEditData;
                  });
                }}
                name="quote-use-inventory"
                label="Use Inventory"
                className="box-max"
                disabled={!accessHeaderFieldsUseInventory.editable}
              />
            )}
            {accessHeaderButtons?.includes("save") && hasEditAndDeleteAccess && (
              <div className="box-max">
                <button
                  className="btn btn-success btn-small"
                  onClick={() => {
                    onUpdateQuote();
                  }}
                  disabled={isLoadingUpdatedQuote}
                >
                  <i
                    className={`${isLoadingUpdatedQuote
                        ? "inline-block animate-spin icon-spin4"
                        : "icon-floppy"
                      } pr-1`}
                  />
                  Save
                </button>
              </div>
            )}
          </div>
        )}
        <div className="row-fluid">
          {!!accessFormEditExpenses && (
            <div className="box-3 edit-quote__misc-card">
              <div className="edit-quote__misc-card-header">Edit Expenses</div>
              <div className="edit-quote__misc-card-content">
                {isLoadingUpdateMiscInfoByQuote && (
                  <LoadingDesign smallLoading={true} className="h-max" />
                )}
                {!isLoadingUpdateMiscInfoByQuote &&
                  !!expensesBuQuoteList.length &&
                  expensesBuQuoteList.map((expense) => (
                    <div
                      className="edit-quote__misc-card-content-item"
                      key={`quote-expense-id-${expense.id}`}
                    >
                      <div className="value">
                        <div>{expense.expenseName}</div>
                        <div>{expense.value}</div>
                      </div>
                      {hasEditAndDeleteAccess && (
                        <div className="btn-group actions">
                          <button
                            className="btn btn-success btn-small"
                            onClick={() => {
                              setCurrentExpensesId(expense.id);
                            }}
                          >
                            <i className={`icon-pencil`} />
                          </button>
                          <button
                            className="btn btn-warning btn-small"
                            onClick={() => {
                              setDeleteId({
                                type: "expense",
                                id: expense.id,
                              });
                            }}
                          >
                            <i className={`icon-trash-empty`} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                {hasEditAndDeleteAccess && (
                  <div className="selects">
                    <SelectFromServerData
                      value={currentExpense.expensesId}
                      onChange={(value) => {
                        let currentValue = value;

                        if (typeof currentValue === "string") {
                          currentValue = +currentValue;
                        }

                        if (currentValue) {
                          setCurrentExpense({
                            ...currentExpense,
                            expensesId: currentValue,
                          });
                        }
                      }}
                      type="expenses"
                      disabled={!accessFieldsSelectExpense}
                    />
                    <Input
                      value={currentExpense.value}
                      onChange={(newValue) => {
                        setCurrentExpense({
                          ...currentExpense,
                          value: newValue.toString().length ? +newValue : "",
                        });
                      }}
                      name="expense-quote-value"
                      type="number"
                      pattern={regExpNumber}
                      disabled={!accessFieldsValueExpense}
                    />
                  </div>
                )}
                {!!accessButtonsExpense && hasEditAndDeleteAccess && (
                  <div className="btn-group">
                    {!!accessButtonsExpense?.includes("saveExpense") && (
                      <button
                        className="btn btn-success btn-small"
                        disabled={
                          isLoadingUpdateExpense ||
                          !currentExpense.value.toString()
                        }
                        onClick={() => {
                          onSaveExpense();
                        }}
                      >
                        <i
                          className={`${isLoadingUpdateExpense
                              ? "inline-block animate-spin icon-spin4"
                              : "icon-floppy"
                            } pr-1`}
                        />
                        {`${currentExpensesId ? "Update" : "Save"} expense`}
                      </button>
                    )}
                    {!!accessButtonsExpense?.includes("clear") && (
                      <button
                        className="btn btn-small"
                        onClick={() => {
                          clearEditDataExpense();
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {!!accessFormOrderCredit && (
            <div className="box-3 edit-quote__misc-card">
              <div className="edit-quote__misc-card-header">
                <i className="icon-mail pr-1" />
                Other Credits
              </div>
              <div className="edit-quote__misc-card-content">
                {isLoadingUpdateMiscInfoByQuote && (
                  <LoadingDesign smallLoading={true} className="h-max" />
                )}
                {!isLoadingUpdateMiscInfoByQuote &&
                  !!creditByQuoteList.length &&
                  creditByQuoteList.map((quoteCreditsByQuoteItem) => (
                    <div
                      key={`credit-by-quote-${quoteCreditsByQuoteItem.id}`}
                      className="edit-quote__misc-card-content-item"
                    >
                      {quoteCreditsByQuoteItem.credit}
                    </div>
                  ))}
              </div>
            </div>
          )}
          {!!accessFormOrderCredit && (
            <div className="box-3 edit-quote__misc-card">
              <div className="edit-quote__misc-card-header">
                <i className="icon-mail pr-1" />
                Cashback
              </div>
              <div className="edit-quote__misc-card-content">
                {isLoadingUpdateMiscInfoByQuote && (
                  <LoadingDesign smallLoading={true} className="h-max" />
                )}
                {!isLoadingUpdateMiscInfoByQuote && (
                  <div className="flex gap-1">
                    <Input
                      value={cashback}
                      onChange={(newValue) => {
                        if (Number(newValue).toString().length > 0)
                          setCashback(Number(newValue));
                      }}
                      name="cashback-value"
                      type="number"
                      pattern={regExpNumber}
                    />
                    <button
                      className="btn btn-success btn-small"
                      disabled={isLoadingSaveCashback}
                      onClick={() => {
                        onSaveCashback();
                      }}
                    >
                      <i
                        className={`${isLoadingSaveCashback
                            ? "inline-block animate-spin icon-spin4"
                            : "icon-floppy"
                          } pr-1`}
                      />
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!!accessFormFiles && !isSupplierReadOnly && (
            <div className="box-3 edit-quote__misc-card">
              <EditQuoteUploadRequestEmail
                quoteId={quoteEditData.id}
                canSend={hasEditAndDeleteAccess}
              />
            </div>
          )}
        </div>

        <div className="row-fluid">
          {accessFormFiles && (
            <FilesCard
              title="Purchase Order & Miscellaneous Files"
              iconClass="icon-doc"
              className="box-6"
              inputId="po-file-input"
              files={filesByQuoteList.filter(
                (file) => !file.availableForSupplier && !file.client,
              )}
              directories={directoriesByQuoteList}
              availableForSupplier={false}
              isLoading={isLoadingUpdateMiscInfoByQuote}
              canManage={hasEditAndDeleteAccess && !isSupplierReadOnly}
              canUpload={canUploadFiles}
              currentFileId={currentFileId}
              onSelectFile={onSetCurrentFileId}
              onClearSelection={() => setCurrentFileId(undefined)}
              onRequestDelete={(id) => setDeleteId({ type: "file", id })}
              onRequestDeleteDirectory={(id) =>
                setDeleteId({ type: "directory", id })
              }
              onMoveFile={onMoveFile}
              onMoveDirectory={onMoveDirectory}
              onAddDirectory={(parentId, name) =>
                onAddDirectory(parentId, false, name)
              }
              onRenameDirectory={onRenameDirectory}
              uploadDraft={uploadDraft}
              setUploadDraft={setUploadDraft}
              fileComment={fileComment}
              setFileComment={setFileComment}
              uploadTarget={uploadTarget}
              onSetUploadTargetDirectory={setUploadTarget}
              runKey={poRunKey}
              onStartUpload={onCreateFiles}
              onCancelUpload={() => cancelRun(poRunKey)}
              onRetryFailed={() => retryFailed(poRunKey)}
              uploadDisabledReason={uploadDisabledReason}
              editingComment={editFileComment}
              setEditingComment={setEditFileComment}
              onSaveComment={onUpdateFileComment}
              isSavingComment={isSavingFileComment}
              onDownloadZip={(directoryId) => onDownloadZip(false, directoryId)}
              downloadingZipKey={downloadingZipKey}
            />
          )}
          {accessFormSupplierFiles && (
            <FilesCard
              title="Data Upload Files"
              iconClass="icon-doc"
              className="box-6"
              inputId="supplier-file-input"
              files={supplierFilesByQuoteList}
              directories={directoriesByQuoteList}
              availableForSupplier={true}
              isLoading={isLoadingUpdateMiscInfoByQuote}
              canManage={hasEditAndDeleteAccess && !isSupplierReadOnly}
              canUpload={canUploadSupplierFiles}
              currentFileId={currentSupplierFileId}
              onSelectFile={onSetCurrentSupplierFileId}
              onClearSelection={() => setCurrentSupplierFileId(undefined)}
              onRequestDelete={(id) => setDeleteId({ type: "file", id })}
              onRequestDeleteDirectory={(id) =>
                setDeleteId({ type: "directory", id })
              }
              onMoveFile={onMoveFile}
              onMoveDirectory={onMoveDirectory}
              onAddDirectory={(parentId, name) =>
                onAddDirectory(parentId, true, name)
              }
              onRenameDirectory={onRenameDirectory}
              uploadDraft={supplierUploadDraft}
              setUploadDraft={setSupplierUploadDraft}
              fileComment={supplierFileComment}
              setFileComment={setSupplierFileComment}
              uploadTarget={supplierUploadTarget}
              onSetUploadTargetDirectory={setSupplierUploadTarget}
              runKey={supplierRunKey}
              onStartUpload={onCreateSupplierFiles}
              onCancelUpload={() => cancelRun(supplierRunKey)}
              onRetryFailed={() => retryFailed(supplierRunKey)}
              uploadDisabledReason={uploadDisabledReason}
              editingComment={editSupplierFileComment}
              setEditingComment={setEditSupplierFileComment}
              onSaveComment={onUpdateSupplierFileComment}
              isSavingComment={isSavingSupplierFileComment}
              onDownloadZip={(directoryId) => onDownloadZip(true, directoryId)}
              downloadingZipKey={downloadingZipKey}
              isCustomerUploadCard
              customerUploadBlockedAt={quoteEditData.customerUploadBlockedAt}
              canToggleCustomerUpload={canToggleCustomerUpload}
              isTogglingCustomerUpload={isTogglingCustomerUpload}
              onToggleCustomerUpload={setCustomerUploadBlockPending}
            />
          )}
        </div>

        <div className="row-fluid">
          {!!accessFormComments && (
            <div className="box-6 edit-quote__misc-card">
              <div className="edit-quote__misc-card-header">
                <i className="icon-comment pr-1" />
                Comments
              </div>

              <div className="edit-quote__misc-card-content">
                {isLoadingUpdateMiscInfoByQuote && (
                  <LoadingDesign smallLoading={true} className="h-max" />
                )}
                {!isLoadingUpdateMiscInfoByQuote &&
                  !!commentsByQuoteList.length && (
                    <div className="quote-comments-list" role="region" aria-label="Quote comments" tabIndex={0}>
                      {commentsByQuoteList.map((comment) => (
                        <div
                          className="edit-quote__misc-card-content-item"
                          key={`quote-comment-id-${comment.id}`}
                        >
                          <div className="comment">
                            <span className="dsc">{`[${convertDateToUSFormat(
                              comment.createdAt,
                            )}] ${comment.user.userName}: `}</span>
                            <span>{`${comment.text}`}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                {hasEditAndDeleteAccess && (
                  <Fragment>
                    <Input
                      value={commentText}
                      onChange={(value) => {
                        setCommentText(value.toString());
                      }}
                      name="quote-comment-input"
                      isTextarea={true}
                      rowCount={2}
                      disabled={!accessFormFieldsValueComments}
                    />
                    {!!accessButtonsComments && (
                      <div className="btn-group">
                        {accessButtonsComments?.includes("addComment") && (
                          <button
                            className="btn btn-success btn-small"
                            disabled={isLoadingUpdateComment || !commentText}
                            onClick={() => {
                              onCreateComment();
                            }}
                          >
                            <i
                              className={`${isLoadingUpdateComment
                                  ? "inline-block animate-spin icon-spin4"
                                  : "icon-plus"
                                } pr-1`}
                            />
                            Add Comment
                          </button>
                        )}
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            </div>
          )}
          {!!accessFormMessage && (
            <div className="box-6 edit-quote__misc-card">
              <div className="edit-quote__misc-card-header">
                <i className="icon-comment pr-1" />
                Message Board
              </div>

              <div className="edit-quote__misc-card-content">
                {isLoadingUpdateMiscInfoByQuote && (
                  <LoadingDesign smallLoading={true} className="h-max" />
                )}
                {!isLoadingUpdateMiscInfoByQuote &&
                  !!messageByQuoteList.length &&
                  messageByQuoteList.map((message) => (
                    <div
                      className="edit-quote__misc-card-content-item"
                      key={`quote-comment-id-${message.id}`}
                    >
                      <div className="comment">
                        <span className="dsc">{`[${convertDateToUSFormat(
                          message.createdAt,
                        )}] ${message.user.userName}: `}</span>
                        <span>{`${message.text}`}</span>
                      </div>
                    </div>
                  ))}
                {hasEditAndDeleteAccess && (
                  <Fragment>
                    <Input
                      value={messageText}
                      onChange={(value) => {
                        setMessageText(value.toString());
                      }}
                      name="quote-comment-input"
                      isTextarea={true}
                      rowCount={2}
                      disabled={!accessFormFieldsValueMessages}
                    />
                    {!!accessButtonsMessages && (
                      <div className="btn-group">
                        {accessButtonsMessages?.includes("addMessage") && (
                          <button
                            className="btn btn-success btn-small"
                            disabled={isLoadingUpdateMessage || !messageText}
                            onClick={() => {
                              onCreateMessage();
                            }}
                          >
                            <i
                              className={`${isLoadingUpdateMessage
                                  ? "inline-block animate-spin icon-spin4"
                                  : "icon-plus"
                                } pr-1`}
                            />
                            Add Message
                          </button>
                        )}
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            </div>
          )}
        </div>
        {!!deleteId && (
          <QuoteDeleteModal
            setShowDeleteModal={() => {
              setDeleteId(undefined);
            }}
            deleteTitle={`Delete quote ${deleteId.type}`}
            onDelete={onDelete}
            loading={isLoadingDelete}
          />
        )}
        {customerUploadBlockPending !== undefined && (
          <ConfirmCustomerUploadModal
            isBlocking={customerUploadBlockPending}
            onClose={() => setCustomerUploadBlockPending(undefined)}
            onApply={onApplyCustomerUploadBlock}
            loading={isTogglingCustomerUpload}
          />
        )}
      </section>
    );
  };

export default EditQuoteMisc;
